<script setup>
import { ref, onMounted, computed } from 'vue';
import { useStore } from './store.js';
import api from './ApiClient.js';
import ThemeManager from './ThemeManager.js';
import AppHeader from './components/Header.vue';
import MovieForm from './components/MovieForm.vue';
import Modal from './components/Modal.vue';
import JobHandler from './components/JobHandler.vue';
import MovieList from './components/MovieList.vue';
import NewMovie from './components/NewMovie.vue';
import AuthModal from './components/AuthModal.vue';

defineProps({ schema: Object });

const { store, loadMovies, loadMovie, clearCurrentMovie } = useStore();

const currentHash = ref(window.location.hash);
const showAuthModal = ref(false);
const user = ref(null);
const isAuthenticated = ref(false);

// Theme management is now handled by ThemeManager

onMounted(async () => {
  // Check for existing authentication
  const token = localStorage.getItem('authToken');
  const userData = localStorage.getItem('user');

  if (token && userData) {
    try {
      // Validate token
      const data = await api.validateToken();
      user.value = data.user;
      isAuthenticated.value = true;
      ThemeManager.applyUserTheme(data.user);
    } catch (error) {
      console.error('Token validation error:', error);
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  } else {
    // Apply system theme for unauthenticated users
    ThemeManager.applySystemTheme();
  }

  if (isAuthenticated.value) {
    await loadMovies();
  }

  window.addEventListener('hashchange', onHashChange);
  handleHashChange();
});

function onHashChange() {
  currentHash.value = window.location.hash;
  handleHashChange();
}

async function handleHashChange() {
  if (currentHash.value.startsWith('#edit/')) {
    const id = currentHash.value.substring(6);
    await loadMovie(id);
  } else if (currentHash.value === '#new-movie') {
    clearCurrentMovie();
  }
}

function handleSelectMovie(id) {
  window.location.hash = 'edit/' + id;
}

function handleAuthSuccess(authData) {
  user.value = authData.user;
  isAuthenticated.value = true;
  ThemeManager.applyUserTheme(authData.user);

  // Refresh the entire page to ensure all content is properly loaded
  // This handles cases where user was viewing a movie before login
  window.location.reload();
}

function handleUserUpdated(updatedUser) {
  user.value = updatedUser;
  ThemeManager.applyUserTheme(updatedUser);
}

async function handleLogout() {
  try {
    await api.logout();
  } catch (error) {
    console.error('Logout error:', error);
  }

  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  user.value = null;
  isAuthenticated.value = false;
  store.movies = [];
  store.currentMovie = null;

  // Apply system theme after logout
  ThemeManager.applySystemTheme();
}

const currentView = computed(() => {
  if (!currentHash.value || currentHash.value === '#') {
    return 'list';
  } else if (currentHash.value === '#new-movie') {
    return 'new';
  } else if (currentHash.value.startsWith('#edit/')) {
    return 'edit';
  }
  return 'list';
});
</script>

<template>
  <AppHeader :user="user" :is-authenticated="isAuthenticated" @login="showAuthModal = true" @logout="handleLogout" @user-updated="handleUserUpdated" />

  <div v-if="!isAuthenticated" class="auth-required">
    <div class="auth-message">
      <h2>Welcome to Film Manager</h2>
      <p>Please log in to access your movies and manage your collection.</p>
      <button @click="showAuthModal = true" class="btn-primary">Login or Register</button>
    </div>
  </div>

  <div v-else class="main-content">
    <main class="content">
      <div v-if="store.loading" class="loading">Loading...</div>
      <div v-if="store.error" class="error-message">{{ store.error }}</div>
      <JobHandler />

      <MovieList v-if="currentView === 'list'" :movies="store.movies" @select-movie="handleSelectMovie" />

      <NewMovie v-else-if="currentView === 'new'" :schema="schema" />

      <MovieForm v-else-if="currentView === 'edit' && store.currentMovie" :schema="schema" :movie="store.currentMovie" :is-creating-new="false" />
    </main>
  </div>

  <AuthModal :show="showAuthModal" @close="showAuthModal = false" @auth-success="handleAuthSuccess" />
  <Modal />
</template>

<style scoped>
.auth-required {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem;
}

.auth-message {
  text-align: center;
  max-width: 400px;
}

.auth-message h2 {
  margin-bottom: 1rem;
  color: #333;
}

.auth-message p {
  margin-bottom: 2rem;
  color: #666;
  line-height: 1.5;
}

.btn-primary {
  background: #007bff;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  background: #0056b3;
}
</style>
